import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import './Checkout.css';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

const planDetails = {
    'basic':   { title: 'Monthly Plan', price: 6999, chats: 30 },
    'premium': { title: 'Yearly Plan', price: 21999, chats: 30 }
};

function Checkout() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planId = searchParams.get('plan') || 'basic';
    const plan = planDetails[planId] || planDetails['basic'];
    const [isProcessing, setIsProcessing] = useState(false);

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponMessage, setCouponMessage] = useState({ text: '', type: '' });
    const [availableCoupons, setAvailableCoupons] = useState([]);

    useEffect(() => {
        const fetchAvailableCoupons = async () => {
            try {
                const response = await fetch(`${API_ENDPOINTS.LIST_COUPONS}?plan=${planId}`);
                const data = await response.json();
                if (data.success) {
                    setAvailableCoupons(data.coupons || []);
                }
            } catch (err) {
                console.error("Failed to fetch coupons:", err);
            }
        };
        fetchAvailableCoupons();
    }, [planId]);

    const handleApplyCoupon = async (codeOverride = null) => {
        // Fix: If called as an event handler, codeOverride will be the event object.
        // We only want to use codeOverride if it's explicitly passed as a string.
        const inputCode = (codeOverride && typeof codeOverride === 'string') ? codeOverride : couponCode;
        const code = inputCode.toUpperCase().trim();
        if (!code) return;

        try {
            const response = await fetch(API_ENDPOINTS.VALIDATE_COUPON, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ code })
            });
            const data = await response.json();

            if (data.success) {
                setAppliedCoupon(data.coupon);
                setCouponCode(code); // Ensure input matches if it was an override
                setCouponMessage({ text: 'Coupon applied successfully!', type: 'success' });
            } else {
                setAppliedCoupon(null);
                setCouponMessage({ text: data.error || 'Invalid coupon code.', type: 'error' });
            }
        } catch (err) {
            setCouponMessage({ text: 'Validation failed. Try again.', type: 'error' });
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponMessage({ text: '', type: '' });
    };

    const calculateTotal = () => {
        const basePrice = Number(plan.price);
        let discountAmount = 0;

        if (appliedCoupon) {
            const discountVal = Number(appliedCoupon.discount);
            if (appliedCoupon.type === 'flat') {
                discountAmount = discountVal;
            } else if (appliedCoupon.type === 'percent') {
                discountAmount = (basePrice * discountVal) / 100;
            }
        }

        const finalTotal = Math.max(0, Math.round(basePrice - discountAmount));
        return { total: finalTotal, discountAmount: Number(discountAmount.toFixed(2)) };
    };

    const { total, discountAmount } = calculateTotal();

    const handlePayment = async (e) => {
        e.preventDefault();
        
        if (!window.Razorpay) {
            alert("Razorpay SDK not loaded. Please refresh the page.");
            return;
        }

        setIsProcessing(true);
        const isRecurring = planId === 'basic'; // only monthly plan is recurring
        
        // ─── If Total is 0, skip Razorpay and grant access directly ───
        if (total === 0) {
            try {
                const response = await fetch(API_ENDPOINTS.CLAIM_FREE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        plan_id: planId,
                        email:   user?.email || '',
                        user_id: user?.id || '',
                        coupon_code: appliedCoupon?.code || ''
                    })
                });
                const data = await response.json();
                if (data.success) {
                    if (data.user) login(data.user);
                    navigate('/chat?payment=success');
                    return;
                } else {
                    throw new Error(data.error || "Failed to claim free access");
                }
            } catch (err) {
                alert("Error: " + err.message);
                setIsProcessing(false);
                return;
            }
        }

        try {
            // STEP 1: Create Order on Backend (First flexible payment)
            const orderResponse = await fetch(API_ENDPOINTS.CREATE_ORDER, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount:       total * 100, // Razorpay expects amount in paise
                    plan_id:      planId,
                    email:        user?.email || '',
                    user_id:      user?.id || '',
                    coupon_code:  appliedCoupon?.code || '',
                    is_recurring: isRecurring
                })
            });

            const orderData = await orderResponse.json();
            if (!orderData.success) {
                throw new Error(orderData.error || "Failed to create order");
            }

            // STEP 2: Open Razorpay Checkout
            // If backend returned a subscription_id, we use it for a direct single-step flow.
            // Otherwise, we use the order_id for a two-step flow (Order first, then Mandate).
            const options = {
                key:             orderData.key,
                name:            "Talk to Krishna India",
                description:     orderData.subscription_id ? "Monthly Subscription Activation" : `First Payment for ${plan.title}`,
                image:           "/logo192.png",
                ...(orderData.subscription_id ? {
                    subscription_id: orderData.subscription_id
                } : {
                    amount:          orderData.amount,
                    currency:        orderData.currency,
                    order_id:        orderData.order_id
                }),
                handler:  async function (response) {
                    // STEP 3: Verify Payment on Backend
                    try {
                        const verifyRes = await fetch(API_ENDPOINTS.VERIFY_PAYMENT || '/api/payments/verify', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id:        response.razorpay_order_id || null,
                                razorpay_subscription_id: response.razorpay_subscription_id || orderData.subscription_id || null,
                                razorpay_payment_id:      response.razorpay_payment_id,
                                razorpay_signature:       response.razorpay_signature
                            })
                        });

                        const verifyData = await verifyRes.json();
                        if (verifyData.success) {
                            if (verifyData.user) login(verifyData.user);

                            // STEP 4: Handle Subscription Mandate if applicable
                            if (verifyData.subscription_id) {
                                console.log("📦 [Subscription] ID received:", verifyData.subscription_id);
                                console.log("⏳ Waiting for first modal to close...");
                                
                                // Give a small delay to ensure the first modal is completely gone
                                setTimeout(() => {
                                    handleMandateSetup(verifyData.subscription_id, orderData.key);
                                }, 1000);
                            } else {
                                // One-time payment done
                                navigate('/chat?payment=success');
                            }
                        } else {
                            alert("Verification failed: " + verifyData.error);
                            setIsProcessing(false);
                        }
                    } catch (err) {
                        alert("Error verifying payment.");
                        setIsProcessing(false);
                    }
                },
                prefill: {
                    name:  user?.name || '',
                    email: user?.email || '',
                },
                notes: orderData.metadata,
                theme: {
                    color: "#f4a261"
                },
                modal: {
                    ondismiss: function () {
                        setIsProcessingUnset();
                        console.log("Payment modal dismissed");
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response) {
                alert("Payment Failed: " + response.error.description);
                setIsProcessing(false);
            });
            rzp.open();

        } catch (err) {
            console.error("Payment error:", err);
            alert("Checkout error: " + err.message);
            setIsProcessing(false);
        }
    };

    const setIsProcessingUnset = () => {
        setIsProcessing(false);
    };

    /**
     * STEP 4: Trigger second popup for Subscription Mandate approval
     *
     * KEY INSIGHT: `callback_url` in Razorpay is a SERVER-SIDE webhook URL.
     * Razorpay POSTs payment data to it after mandate authorization.
     * Pointing it at a React frontend URL (/chat?...) causes Razorpay to receive
     * an HTML page response → Razorpay treats this as a FAILURE → shows error modal.
     *
     * CORRECT approach: Use `handler` only (client-side callback).
     * After mandate, handler fires → navigate to success page.
     */
    const handleMandateSetup = (subscriptionId, razorpayKey) => {
        const subOptions = {
            key: razorpayKey,
            subscription_id: subscriptionId,
            name: "Talk to Krishna India",
            description: "Authorize Auto-debit for Subscription",
            image: "/logo192.png",
            handler: function (response) {
                console.log("✅ Mandate authorized!", response);
                // Fire-and-forget background sync — navigate immediately, don't await
                fetch(API_ENDPOINTS.VERIFY_SUBSCRIPTION, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subscription_id: subscriptionId,
                        user_id: user?.id
                    })
                }).catch(err => console.error("Background sync error:", err));

                navigate('/chat?payment=success&subscription=active');
            },
            prefill: {
                name: user?.name || '',
                email: user?.email || '',
            },
            notes: {
                subscription_id: subscriptionId,
                user_id: user?.id
            },
            theme: {
                color: "#f4a261"
            },
            modal: {
                ondismiss: function () {
                    console.log("Mandate modal dismissed by user");
                    alert("First payment was successful, but autopay mandate was not completed. You can complete it anytime from your Profile page.");
                    navigate('/chat');
                }
            }
        };

        const rzpSub = new window.Razorpay(subOptions);
        rzpSub.on('payment.failed', function (response) {
            console.error("Mandate payment failed:", response.error);
            alert("Mandate setup failed: " + response.error.description + ". You can retry from your Profile page.");
            navigate('/chat');
        });
        console.log("🚀 Triggering mandate popup for subscription:", subscriptionId);
        rzpSub.open();
    };

    return (
        <div className="checkout-page">
            <Navbar />

            <div className="checkout-container fade-in-section animate-in">
                <div className="checkout-header">
                    <h2>Complete Your Purchase</h2>
                    <p>Secure checkout to begin your spiritual journey</p>
                </div>

                <div className="checkout-content">

                    {/* Right Side: Order Summary */}
                    <div className="checkout-summary-section">
                        <h3>Order Summary</h3>

                        <div className="summary-card">
                            <div className="summary-item main-item">
                                <span>{plan.title}</span>
                                <span>₹{plan.price}</span>
                            </div>

                            <hr className="summary-divider" />

                            <div className="coupon-section">
                                <label>Have a coupon code?</label>
                                <div className="coupon-input-group">
                                    <input
                                        type="text"
                                        placeholder="ENTER CODE"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        disabled={appliedCoupon !== null}
                                        className="premium-input"
                                    />
                                    {!appliedCoupon ? (
                                        <button className="btn-apply-coupon" onClick={handleApplyCoupon}>Apply</button>
                                    ) : (
                                        <button className="btn-remove-coupon" onClick={removeCoupon}>Remove</button>
                                    )}
                                </div>
                                {couponMessage.text && (
                                    <div className={`coupon-message ${couponMessage.type}`}>
                                        {couponMessage.text}
                                    </div>
                                )}

                                {availableCoupons.length > 0 && !appliedCoupon && (
                                    <div className="available-coupons-section">
                                        <p className="available-label">Available Coupons:</p>
                                        <div className="coupons-grid">
                                            {availableCoupons.map(coupon => (
                                                <div 
                                                    key={coupon.code} 
                                                    className="coupon-tag"
                                                    onClick={() => handleApplyCoupon(coupon.code)}
                                                >
                                                    <span className="coupon-code-text">{coupon.code}</span>
                                                    <span className="coupon-benefit">
                                                        {coupon.type === 'percent' ? `${coupon.discount}% OFF` : `₹${coupon.discount} OFF`}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <hr className="summary-divider" />

                            <div className="summary-item">
                                <span>Subtotal</span>
                                <span>₹{plan.price}</span>
                            </div>

                            {appliedCoupon && (
                                <div className="summary-item discount animate-fade-in">
                                    <span>
                                        Discount ({appliedCoupon.code})
                                        <small style={{ display: 'block', fontSize: '0.75rem', opacity: 0.8 }}>
                                            {appliedCoupon.type === 'percent' ? `${appliedCoupon.discount}% OFF` : `₹${appliedCoupon.discount} FLAT OFF`}
                                        </small>
                                    </span>
                                    <span>-₹{discountAmount}</span>
                                </div>
                            )}

                            <hr className="summary-divider mb-3" />

                            <div className="summary-item total">
                                <span>Total to Pay</span>
                                <span>₹{total}</span>
                            </div>

                            <button 
                                className="btn-premium-primary btn-pay-now w-100" 
                                onClick={handlePayment}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : `Pay ₹${total} Securely`}
                            </button>

                            <div className="secure-badge">
                                <span>🔒</span> 100% Secure & Encrypted Transaction
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Checkout;
